import { useId, useRef, useState } from 'react'
import Icon from '../../docs/Icon'

export type FileInputProps = {
  label: string
  hint?: string
  /** File types, as for the input's accept attribute, e.g. ".pdf,.jpg". */
  accept?: string
  /** Largest file allowed, in megabytes. */
  maxMb?: number
  multiple?: boolean
  onChange?: (files: File[]) => void
  id?: string
}

const sizeLabel = (bytes: number) =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`

/** Accepts a file when its extension or type matches one of the accept tokens. */
function accepted(file: File, accept?: string) {
  if (!accept) return true
  return accept.split(',').some((token) => {
    const rule = token.trim().toLowerCase()
    if (rule.startsWith('.')) return file.name.toLowerCase().endsWith(rule)
    if (rule.endsWith('/*')) return file.type.startsWith(rule.slice(0, -1))
    return file.type === rule
  })
}

/**
 * A file field with a drop zone. The real file input stays in the page, so
 * keyboard and screen reader users get the browser's own picker; chosen files
 * are listed with their size and a remove button, and files of the wrong type
 * or size are named in an error.
 */
export default function FileInput({ label, hint, accept, maxMb, multiple = true, onChange, id }: FileInputProps) {
  const autoId = useId()
  const fieldId = id ?? autoId
  const hintId = `${fieldId}-hint`
  const errorId = `${fieldId}-error`
  const [files, setFiles] = useState<File[]>([])
  const [error, setError] = useState('')
  const [dragging, setDragging] = useState(false)
  const input = useRef<HTMLInputElement>(null)

  const add = (incoming: File[]) => {
    const rejected = incoming.filter((file) => !accepted(file, accept) || (maxMb !== undefined && file.size > maxMb * 1024 * 1024))
    const ok = incoming.filter((file) => !rejected.includes(file))
    const next = multiple ? [...files, ...ok] : ok.slice(0, 1)
    setFiles(next)
    onChange?.(next)
    setError(
      rejected.length
        ? `${rejected.map((file) => file.name).join(', ')} ${rejected.length === 1 ? 'is' : 'are'} not ${accept ? `a ${accept} file` : 'allowed'}${maxMb ? ` under ${maxMb} MB` : ''}.`
        : '',
    )
  }

  const remove = (file: File) => {
    const next = files.filter((item) => item !== file)
    setFiles(next)
    onChange?.(next)
    input.current?.focus()
  }

  return (
    <div className="mds-field mds-file">
      <label className="mds-label" htmlFor={fieldId}>
        {label}
      </label>
      {hint && (
        <span className="mds-hint" id={hintId}>
          {hint}
        </span>
      )}
      <div
        className={`mds-file__zone${dragging ? ' is-dragging' : ''}${error ? ' is-invalid' : ''}`}
        onDragOver={(event) => {
          event.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault()
          setDragging(false)
          add(Array.from(event.dataTransfer.files))
        }}
      >
        <Icon name="upload" size={22} />
        <span>
          Drag files here or <span className="mds-file__choose">choose from folder</span>
        </span>
        <input
          ref={input}
          id={fieldId}
          type="file"
          className="mds-file__input"
          accept={accept}
          multiple={multiple}
          aria-invalid={!!error}
          aria-describedby={[hint ? hintId : '', error ? errorId : ''].filter(Boolean).join(' ') || undefined}
          onChange={(event) => {
            add(Array.from(event.target.files ?? []))
            event.target.value = ''
          }}
        />
      </div>
      {error && (
        <span className="mds-error-text" id={errorId} role="alert">
          {error}
        </span>
      )}
      {files.length > 0 && (
        <ul className="mds-file__list" aria-label={`${files.length} ${files.length === 1 ? 'file' : 'files'} chosen`}>
          {files.map((file) => (
            <li key={`${file.name}-${file.size}-${file.lastModified}`} className="mds-file__item">
              <Icon name="file" size={18} />
              <span className="mds-file__name">{file.name}</span>
              <span className="mds-hint">{sizeLabel(file.size)}</span>
              <button type="button" className="mds-button mds-button--ghost mds-button--sm" onClick={() => remove(file)}>
                Remove <span className="mds-visually-hidden">{file.name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
